import { IFkParser, IFkJournalHints } from "./types";

// ── Wspólne hints dziennika dla polskich systemów FK (fallback) ───────────────

export const GENERIC_JOURNAL_HINTS: IFkJournalHints = {
  nrDokumentu:     ["nr dokumentu", "numer dokumentu", "nr dok.", "nr dok", "numer dok", "numer", "dokument"],
  dataKsiegowania: ["data księgowania", "data ksiegowania", "data ks.", "data ks", "data"],
  typDokumentu:    ["typ dokumentu", "rodzaj dokumentu", "typ dok", "rodzaj"],
  opis:            ["treść", "tresc", "opis operacji", "opis dokumentu", "opis", "tytuł", "tytul"],
  // "wn"/"ma" usunięto jako standalone — za krótkie dla h.length ≥ 3 check w detectMapping
  kontoWn:         ["konto wn", "konto_wn", "konto wn.", "winien"],
  kontoMa:         ["konto ma", "konto_ma", "konto ma."],
  kwota:           ["kwota", "wartość", "wartosc", "kwota pln", "suma"],
  pairMode:        "paired",
};

// ── Klucz localStorage dla wybranego systemu FK ───────────────────────────────
// Przeniesiony tu ze wspólnego punktu, żeby oba komponenty (MigrationTab, WpkGminyTab)
// mogły go zaimportować bez cyklicznych zależności.
export const MIGRATION_SYSTEM_KEY = "zois_migration_system";

// ── Rejestr parserów ──────────────────────────────────────────────────────────
// Każdy wpis odpowiada jednemu systemowi FK z listy FIN_SYSTEMS w MigrationTab.
// Dodanie nowego systemu = dopisanie jednego obiektu do tej mapy.

export const FK_PARSERS: Record<string, IFkParser> = {

  // ─────────────────────────────────────────────────────────────────────────
  // WAPRO Fakir
  // ─────────────────────────────────────────────────────────────────────────
  wapro_fakir: {
    id: "wapro_fakir",
    label: "WAPRO Fakir",
    defaultEncoding: "windows-1250",
    defaultCsvSeparator: ";",
    columnHints: {
      code: ["symbol", "nr konta", "numer konta", "konto"],
      name: ["nazwa", "opis", "tytu"],
      boWn: ["bo wn", "b.o. wn", "bilans wn", "saldo wn", "saldo otw. wn"],
      boMa: ["bo ma", "b.o. ma", "bilans ma", "saldo ma", "saldo otw. ma"],
    },
    journalHints: {
      // Nagłówki eksportu dziennika WAPRO Fakir (Księgowość → Dziennik → Eksport do pliku)
      // CK = inicjały operatora (pole statusu) — ignorowane, nie ma odpowiednika w siatce
      nrDokumentu:     [
        "nr dokumentu",          // standardowy nagłówek
        "numer dokumentu",
        "nr dok.",
        "numer dok.",
        "numer dokumentu księg.",
        "symbol dokumentu",
        "nr dziennika",          // w Fakir pozycja w dzienniku może być kluczem
        "numer dziennika",
        "nr pozycji",
        "numer pozycji",
        "numer",                 // krótka forma — min. 5 znaków, bezpieczna
      ],
      dataKsiegowania: [
        "data księgowania",
        "data ks.",
        "data ks",
        "data zaksięgowania",
        "data dok.",             // data dokumentu jako zamiennik jeśli brak daty ks.
        "data",                  // minimalna forma — min. 4 znaki
      ],
      typDokumentu:    [
        "symbol dziennika",      // typowy nagłówek w Fakir
        "dziennik",
        "typ dokumentu",
        "rodzaj dokumentu",
      ],
      opis:            [
        "treść",
        "tresc",
        "opis dokumentu",
        "treść dokumentu",
        "opis operacji",
        "opis",
        "tytuł",
        "tytul",
      ],
      // "wn"/"ma" dopasowują się wyłącznie przez h === patNorm (dokładne trafienie).
      // Filtr h.length >= 3 dotyczy tylko kierunku patNorm.includes(h) — chroni przed
      // "w" (1 znak) pasującym do "konto wn", ale "wn" === "wn" jest bezpieczne.
      kontoWn:         ["konto wn", "konto wn.", "kto wn", "k-wn", "winien", "wn"],
      kontoMa:         ["konto ma", "konto ma.", "kto ma", "k-ma", "ma"],
      kwota:           ["kwota", "kwota pln", "kwota operacji", "wartość", "wartosc"],
      // Fakir eksportuje każdy wiersz jako pełną parę Wn+Ma (jeden wiersz = jeden dekret)
      pairMode:        "paired",
    },
    codeRules: {
      sourceSeparator: "/",         // Fakir rozdziela analitykę ukośnikiem
      padTwoDigitSegments: true,    // "71" → "071"
      validCodePattern: "^\\d[\\d-]*$",
    },
    notes:
      "CSV z separatorem ';'; kodowanie Windows-1250 bez BOM. " +
      "Kody analityczne rozdzielane '/'; 2-cyfrowe segmenty uzupełniane wiodącym zerem. " +
      "Nagłówki BO: 'Symbol konta', 'Nazwa konta', 'BO Wn', 'BO Ma'. " +
      "Nagłówki dziennika: 'Nr dokumentu', 'Data księgowania', 'Treść', 'Konto Wn', 'Konto Ma', 'Kwota'. " +
      "Format dekretów: paired (Konto Wn + Konto Ma w jednym wierszu).",
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Comarch ERP Optima
  // ─────────────────────────────────────────────────────────────────────────
  comarch_optima: {
    id: "comarch_optima",
    label: "Comarch ERP Optima",
    defaultEncoding: "utf-8",
    defaultCsvSeparator: ";",
    columnHints: {
      code: ["numer konta", "konto", "symbol konta", "symbol"],
      name: ["nazwa konta", "nazwa", "opis konta", "opis"],
      boWn: ["bilans wn", "saldo pocz. wn", "saldo wn", "otwarcie wn"],
      boMa: ["bilans ma", "saldo pocz. ma", "saldo ma", "otwarcie ma"],
    },
    codeRules: {
      sourceSeparator: "-",
      padTwoDigitSegments: false,
      validCodePattern: "^\\d[\\d-]*$",
    },
    notes:
      "XLSX lub CSV UTF-8 z ';'. Kody 3-cyfrowe, separator '-', brak problemu wiodących zer. " +
      "Nagłówki: 'Numer konta', 'Nazwa konta', 'Bilans Wn/Ma'.",
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Comarch CDN XL
  // ─────────────────────────────────────────────────────────────────────────
  comarch_xl: {
    id: "comarch_xl",
    label: "Comarch CDN XL",
    defaultEncoding: "utf-8",
    defaultCsvSeparator: ";",
    columnHints: {
      code: ["numer konta", "konto", "symbol konta"],
      name: ["nazwa konta", "nazwa", "opis"],
      boWn: ["bilans wn", "saldo wn", "saldo pocz. wn"],
      boMa: ["bilans ma", "saldo ma", "saldo pocz. ma"],
    },
    codeRules: {
      sourceSeparator: "-",
      padTwoDigitSegments: false,
      validCodePattern: "^\\d[\\d-]*$",
    },
    notes:
      "Format zbliżony do ERP Optima. Eksport do XLSX; " +
      "kolumny i układ mogą się nieznacznie różnić w zależności od wersji.",
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Symfonia Finanse i Księgowość
  // ─────────────────────────────────────────────────────────────────────────
  symfonia_fk: {
    id: "symfonia_fk",
    label: "Symfonia Finanse i Księgowość",
    defaultEncoding: "windows-1250",
    defaultCsvSeparator: ";",
    columnHints: {
      code: ["konto", "symbol konta", "numer konta", "numer"],
      name: ["nazwa", "opis konta", "opis"],
      boWn: ["wn", "saldo wn", "otwarcie wn", "bilans wn"],
      boMa: ["ma", "saldo ma", "otwarcie ma", "bilans ma"],
    },
    codeRules: {
      sourceSeparator: "-",
      padTwoDigitSegments: false,
      validCodePattern: "^\\d[\\d-]*$",
    },
    notes:
      "CSV z ';'; kodowanie Windows-1250. " +
      "Nagłówki: 'Konto', 'Nazwa', 'Wn', 'Ma' lub podobne.",
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Soneta enova365
  // ─────────────────────────────────────────────────────────────────────────
  enova365: {
    id: "enova365",
    label: "Soneta enova365",
    defaultEncoding: "utf-8",
    defaultCsvSeparator: ";",
    columnHints: {
      code: ["konto", "numer konta", "symbol konta", "symbol"],
      name: ["nazwa", "opis"],
      boWn: ["saldo wn", "bo wn", "bilans wn", "wn"],
      boMa: ["saldo ma", "bo ma", "bilans ma", "ma"],
    },
    codeRules: {
      sourceSeparator: "-",
      padTwoDigitSegments: false,
      validCodePattern: "^\\d[\\d-]*$",
    },
    notes:
      "Eksport do XLSX lub CSV UTF-8 z ';'. " +
      "Nagłówki: 'Konto', 'Nazwa', 'Saldo Wn', 'Saldo Ma'.",
  },

  // ─────────────────────────────────────────────────────────────────────────
  // SAP
  // ─────────────────────────────────────────────────────────────────────────
  sap: {
    id: "sap",
    label: "SAP",
    defaultEncoding: "utf-8",
    defaultCsvSeparator: "\t",
    columnHints: {
      code: ["account", "konto", "sachkonto", "g/l account", "gl account"],
      name: ["description", "nazwa", "bezeichnung", "g/l acct long text", "short text"],
      boWn: ["debit", "wn", "soll", "opening debit"],
      boMa: ["credit", "ma", "haben", "opening credit"],
    },
    codeRules: {
      sourceSeparator: null,
      padTwoDigitSegments: false,
      validCodePattern: "^\\d[\\d-]*$",
    },
    notes:
      "Eksport XLSX lub CSV z tabulatorem; kodowanie UTF-8. " +
      "Nagłówki zależne od konfiguracji: angielski, polski lub niemiecki. " +
      "Kody kont zazwyczaj 6-10-cyfrowe, bez separatora analityki.",
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Microsoft Dynamics 365
  // ─────────────────────────────────────────────────────────────────────────
  ms_dynamics: {
    id: "ms_dynamics",
    label: "Microsoft Dynamics 365",
    defaultEncoding: "utf-8",
    defaultCsvSeparator: ",",
    columnHints: {
      code: ["main account", "account number", "konto", "account"],
      name: ["name", "account name", "nazwa"],
      boWn: ["debit", "wn", "balance debit", "opening debit"],
      boMa: ["credit", "ma", "balance credit", "opening credit"],
    },
    codeRules: {
      sourceSeparator: null,
      padTwoDigitSegments: false,
      validCodePattern: "^\\d[\\d-]*$",
    },
    notes:
      "CSV z przecinkiem; kodowanie UTF-8. Nagłówki po angielsku. " +
      "Kody kont w formacie zdefiniowanym przez plan kont wdrożenia.",
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Insert Rewizor GT
  // ─────────────────────────────────────────────────────────────────────────
  rewizor: {
    id: "rewizor",
    label: "Insert Rewizor GT",
    defaultEncoding: "windows-1250",
    defaultCsvSeparator: ";",
    columnHints: {
      code: ["numer", "symbol", "konto", "nr konta"],
      name: ["nazwa", "opis"],
      boWn: ["saldo wn", "wn", "bilans wn", "bo wn"],
      boMa: ["saldo ma", "ma", "bilans ma", "bo ma"],
    },
    codeRules: {
      sourceSeparator: "-",
      padTwoDigitSegments: false,
      validCodePattern: "^\\d[\\d-]*$",
    },
    notes:
      "CSV z ';'; kodowanie Windows-1250. " +
      "Format zbliżony do Wapro Fakir; kody kont 3-cyfrowe z '-'.",
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Insert Rachmistrz GT
  // ─────────────────────────────────────────────────────────────────────────
  rachmistrz: {
    id: "rachmistrz",
    label: "Insert Rachmistrz GT",
    defaultEncoding: "windows-1250",
    defaultCsvSeparator: ";",
    columnHints: {
      code: ["numer", "symbol", "konto", "nr konta"],
      name: ["nazwa", "opis"],
      boWn: ["saldo wn", "wn", "bo wn"],
      boMa: ["saldo ma", "ma", "bo ma"],
    },
    codeRules: {
      sourceSeparator: "-",
      padTwoDigitSegments: false,
      validCodePattern: "^\\d[\\d-]*$",
    },
    notes:
      "Format zbliżony do Rewizor GT — CSV z ';'; kodowanie Windows-1250.",
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Streamsoft Prestiż
  // ─────────────────────────────────────────────────────────────────────────
  streamsoft: {
    id: "streamsoft",
    label: "Streamsoft Prestiż",
    defaultEncoding: "windows-1250",
    defaultCsvSeparator: ";",
    columnHints: {
      code: ["konto", "symbol konta", "numer konta", "nr konta"],
      name: ["nazwa", "opis konta", "opis"],
      boWn: ["saldo wn", "bo wn", "bilans wn", "wn"],
      boMa: ["saldo ma", "bo ma", "bilans ma", "ma"],
    },
    codeRules: {
      sourceSeparator: "-",
      padTwoDigitSegments: false,
      validCodePattern: "^\\d[\\d-]*$",
    },
    notes:
      "CSV z ';'; kodowanie Windows-1250. " +
      "Typowy eksport polskiego systemu FK.",
  },

  // ─────────────────────────────────────────────────────────────────────────
  // RAKS SQL
  // ─────────────────────────────────────────────────────────────────────────
  raks: {
    id: "raks",
    label: "RAKS SQL",
    defaultEncoding: "windows-1250",
    defaultCsvSeparator: ";",
    columnHints: {
      code: ["symbol", "konto", "numer", "nr konta"],
      name: ["nazwa", "opis"],
      boWn: ["saldo wn", "wn", "bo wn"],
      boMa: ["saldo ma", "ma", "bo ma"],
    },
    codeRules: {
      sourceSeparator: "-",
      padTwoDigitSegments: false,
      validCodePattern: "^\\d[\\d-]*$",
    },
    notes:
      "CSV z ';'; kodowanie Windows-1250.",
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Inny system (fallback z auto-detekcją)
  // ─────────────────────────────────────────────────────────────────────────
  other: {
    id: "other",
    label: "Inny system",
    defaultEncoding: "auto",
    defaultCsvSeparator: "auto",
    columnHints: {
      code: ["symbol", "nr konta", "numer konta", "konto", "account", "numer"],
      name: ["nazwa", "opis", "name", "description", "tytu"],
      boWn: ["bo wn", "saldo wn", "bilans wn", "wn", "debit", "otwarcie wn"],
      boMa: ["bo ma", "saldo ma", "bilans ma", "ma", "credit", "otwarcie ma"],
    },
    codeRules: {
      sourceSeparator: null,
      padTwoDigitSegments: false,
      validCodePattern: "^\\d[\\d/.-]*$",
    },
    notes:
      "Tryb auto-detekcji: kodowanie wykrywane z BOM, separator z pierwszej linii pliku. " +
      "Stosowany gdy system nie jest na liście lub nie jest wybrany.",
  },
};

// ── Funkcje pomocnicze ────────────────────────────────────────────────────────

/** Zwraca parser dla podanego id systemu; fallback na 'other'. */
export const getParser = (systemId: string | null | undefined): IFkParser =>
  FK_PARSERS[systemId ?? ""] ?? FK_PARSERS["other"];

/** Odczytuje wybrany system FK z localStorage. */
export const loadSelectedFkSystem = (): string | null => {
  try {
    const raw = localStorage.getItem(MIGRATION_SYSTEM_KEY);
    return raw ? (JSON.parse(raw) as { id: string }).id : null;
  } catch {
    return null;
  }
};

/**
 * Normalizuje symbol konta zgodnie z zasadami danego systemu FK.
 * - Zamienia sourceSeparator na "-"
 * - Opcjonalnie uzupełnia wiodące zero (np. "71" → "071")
 */
export const applyCodeNorm = (raw: string, parser: IFkParser): string => {
  if (!raw) return raw;
  let code = raw.trim();
  if (parser.codeRules.sourceSeparator) {
    code = code.split(parser.codeRules.sourceSeparator).join("-");
  }
  if (parser.codeRules.padTwoDigitSegments) {
    const parts = code.split("-");
    if (/^\d{2}$/.test(parts[0])) parts[0] = "0" + parts[0];
    code = parts.join("-");
  }
  return code;
};

/**
 * Sprawdza, czy znormalizowany symbol konta jest prawidłowy
 * według wzorca validCodePattern parsera.
 */
export const isValidCode = (code: string, parser: IFkParser): boolean => {
  if (!code) return false;
  try {
    return new RegExp(parser.codeRules.validCodePattern).test(code);
  } catch {
    return /^\d[\d-]*$/.test(code);
  }
};

/**
 * Wykrywa indeks kolumny na podstawie słów kluczowych.
 * Zwraca -1 jeśli nie znaleziono.
 */
export const detectCol = (headers: string[], keywords: string[]): number =>
  headers.findIndex((h) => keywords.some((kw) => h.toLowerCase().includes(kw)));

/**
 * Wykrywa kodowanie pliku CSV.
 * Jeśli parser ma określone kodowanie (nie "auto"), używa go jako domyślnego
 * (z możliwością nadpisania przez BOM).
 */
export const detectEncoding = (bytes: Uint8Array, parser: IFkParser): string => {
  // BOM zawsze wygrywa
  if (bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) return "utf-8";
  if (bytes[0] === 0xFF && bytes[1] === 0xFE) return "utf-16le";
  if (bytes[0] === 0xFE && bytes[1] === 0xFF) return "utf-16be";
  // Brak BOM → użyj domyślnego parsera lub windows-1250 dla polskich systemów
  if (parser.defaultEncoding !== "auto") return parser.defaultEncoding;
  return "windows-1250";
};

/**
 * Wykrywa separator CSV.
 * Jeśli parser ma określony separator (nie "auto"), używa go.
 * W trybie "auto" zlicza ';' i ',' w pierwszej linii.
 */
export const detectCsvSep = (firstLine: string, parser: IFkParser): string => {
  if (parser.defaultCsvSeparator !== "auto") return parser.defaultCsvSeparator;
  return (firstLine.match(/;/g) ?? []).length >= (firstLine.match(/,/g) ?? []).length
    ? ";"
    : ",";
};
